import { useState } from 'react';
import { getLang, setLang, t, type Lang } from '../../platform/lang';

/**
 * What a new driver sees before their first route.
 *
 * The owner's analogy was the fake camera at a petrol station: make the
 * security visible enough that nobody tries. The difference here is that none
 * of it is fake, and that is precisely why this screen can be SPECIFIC.
 *
 * A vague warning ("this app is monitored") invites a test, and anyone who
 * tests a bluff discovers it. Four exact, checkable statements do not: a driver
 * who works out that the phone really is bound, the face really is compared and
 * the position really is recorded to the foot has learned the system works,
 * not that it is theatre. Twenty-five completions were refused on distance the
 * first day this ran.
 *
 * It closes on the point that matters most to an honest driver — the record
 * protects THEM when a hospital claims a delivery never arrived. A screen that
 * only threatens makes good drivers feel suspected; one that explains makes
 * them an ally and still deters the other kind.
 *
 * Shown once, and it never blocks anything: dismissing it is the only action.
 */

const SEEN = 'jw.securityBriefingSeen';

export function briefingSeen(): boolean {
  try {
    return localStorage.getItem(SEEN) === '1';
  } catch {
    // Storage disabled: show it. Seeing it twice is harmless; never seeing it
    // defeats the point.
    return false;
  }
}

function markSeen(): void {
  try { localStorage.setItem(SEEN, '1'); } catch { /* nothing to do */ }
}

export function SecurityBriefing({ onDone }: { onDone: () => void }) {
  const [lang, setLangState] = useState<Lang>(getLang);

  const pick = (l: Lang) => { setLangState(l); setLang(l); };
  const s = (k: Parameters<typeof t>[0]) => t(k, undefined, lang);

  const POINTS: Array<[string, string]> = [
    [s('secPhoneTitle'), s('secPhoneBody')],
    [s('secFaceTitle'), s('secFaceBody')],
    [s('secGpsTitle'), s('secGpsBody')],
    [s('secKeepTitle'), s('secKeepBody')],
  ];

  return (
    <main className="screen">
      {/* The switch sits at the top: a driver who cannot read the page needs it
          before anything else on the page, not after. */}
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button className={lang === 'en' ? 'btn small primary' : 'btn small secondary'}
                onClick={() => pick('en')} aria-pressed={lang === 'en'}>
          English
        </button>
        <button className={lang === 'es' ? 'btn small primary' : 'btn small secondary'}
                onClick={() => pick('es')} aria-pressed={lang === 'es'}>
          Español
        </button>
      </div>

      <h1>{s('secTitle')}</h1>
      <p className="hint">{s('secIntro')}</p>

      {POINTS.map(([title, body], i) => (
        <section key={title} className="help-card">
          <span className="help-step">{i + 1}</span>
          <div>
            <h2>{title}</h2>
            <p className="hint">{body}</p>
          </div>
        </section>
      ))}

      <p className="muted small">{s('secWhy')}</p>

      <button className="btn primary big"
              onClick={() => { markSeen(); onDone(); }}>
        {s('secAck')}
      </button>
    </main>
  );
}
